import { BusinessHoursForm, type Schedule } from "@/features/schedule/business-hours-form"
import { PROMOTION_PATHS, tPromotion } from "@/features/staff/promotions/promotions"

type Props = {
  promotionId: number
  window: Schedule
  timeZone: string | null
}

/**
 * The weekly window a rule applies inside.
 *
 * **This is the business-hours editor, not a second one.** A promotion window is
 * a `Schedule` and `Schedule#apply_week!` replaces the whole week in one
 * transaction, which is exactly what that editor already speaks. A second weekly
 * grid would be a second place to get overnight periods and spring-forward days
 * wrong, and `Location#business_hours` already paid for getting them right.
 *
 * **No time-zone selector, and the omission is the design.** A `Schedule` copies
 * `time_zone` from its schedulable once, at build time, and nothing syncs it
 * afterwards. On the business-hours screen that column IS the restaurant's zone
 * and editing it is the point; here the rule borrows `Location#time_zone`, so an
 * editable field would let "martes de 3 a 6" drift into meaning two different
 * hours depending on which row the reader consults. Omitting the `timezones`
 * prop is what hides the control, and
 * `Staff::Location::Manage::PromotionSchedulesController` refuses to permit the
 * parameter regardless — the screen not offering it is the courtesy, the
 * controller not taking it is the guarantee.
 *
 * The zone is stated in a line of copy instead, because a manager setting "3pm"
 * is entitled to know which 3pm the till will read.
 */
export function PromotionSchedule({ promotionId, window, timeZone }: Props) {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm font-medium">{tPromotion("window_title")}</p>
        <p className="text-muted-foreground text-xs">{tPromotion("window_description")}</p>
        {timeZone && (
          <p className="text-muted-foreground text-xs">
            {tPromotion("window_time_zone", { zone: timeZone })}
          </p>
        )}
      </div>

      <BusinessHoursForm
        schedule={window}
        url={PROMOTION_PATHS.schedule(promotionId)}
        labels={{
          enable: tPromotion("window_enable"),
          enableHint: tPromotion("window_enable_hint"),
        }}
        testIdPrefix={`promotion-window-${promotionId}`}
      />
    </div>
  )
}
